import pluralize from "pluralize";
import React, { Fragment, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { FileRow } from "../../../common/components";
import { HorizontalDivider } from "../../../common/components/core";
import { PurposeCodeOptions, CNYPurposeCodeOptions } from "../../../constants/purposeCodes";
import { useSelector } from "../../../redux/hooks";
import {
    currentRolesSelector,
    currentUserEmailSelector,
} from "../../../redux/slices/auth/selectors";
import {
    PaymentStatus,
    PaymentOrderStatus,
    PreAndPaymentStateWithDocuments,
} from "../../../types/payment";
import { HistoryItem } from "../../../types/common";
import { formatAmount } from "../../../utils/amount";
import { formatToDate } from "../../../utils/common";

import HousingRulesText from "./HousingRulesText";
import PreAndPaymentStatusDetail from "./PreAndPaymentStatusDetail";
import { PaymentApproversTable } from "./PaymentApproversTable";
import useIsPreviousApproverApproved from "../hooks/useIsPreviousApproverApproverd";
import { isUserFinanceDepartment } from "../../../utils/role";

type PaymentOrderDetailProps = {
    preAndPayment: PreAndPaymentStateWithDocuments;
    preSubmitPaymentHistory: HistoryItem<PaymentOrderStatus>[];
    closed: boolean;
};

const PaymentOrderDetail = ({
    preAndPayment,
    preSubmitPaymentHistory,
    closed,
}: PaymentOrderDetailProps): JSX.Element => {
    const currentUserEmail = useSelector(currentUserEmailSelector);
    const currentRoles = useSelector(currentRolesSelector);
    const { paymentOrderState, paymentState, sharedDocument } = preAndPayment;
    const isFailed =
        paymentState?.status === PaymentStatus.FAILED &&
        !isUserFinanceDepartment(currentRoles);
    const developerAccountName = paymentOrderState.props.payer.externalRef;
    const contractorAccountName = paymentOrderState.props.payee.externalRef;
    const paymentApprovers = preAndPayment.paymentOrderState.props.approvers;

    const isPreviousPriorityApproverApproved = useIsPreviousApproverApproved(
        paymentApprovers ?? [],
    );
    const currentPaymentApprovers = useMemo(() => {
        return paymentApprovers.find(
            (it) => it.details.email === currentUserEmail,
        );
    }, [paymentApprovers, currentUserEmail]);
    return (
        <Card
            className={`mb-5 border-0 ${
                isFailed ? "text-placeholder-grey" : ""
            }`}
        >
            <Card.Header className="p-0 pb-3">
                <h2 className="mb-0">
                    Payment Order{" "}
                    <span className="fs-7 fw-bold">
                        #{paymentOrderState.props.customerRefNumber}
                    </span>
                    <PreAndPaymentStatusDetail
                        roles={currentRoles}
                        preSubmitPaymentHistory={preSubmitPaymentHistory}
                        latestPreSubmitPaymentStatus={paymentOrderState.status}
                        latestPaymentStatus={paymentState?.status}
                        approverStatus={currentPaymentApprovers?.status}
                        isPreviousPriorityApproverApproved={
                            isPreviousPriorityApproverApproved
                        }
                        closed={closed}
                    />
                </h2>
            </Card.Header>

            <Card.Body className="px-0 py-0">
                <dl>
                    <div>
                        <StyledH3 isFailed={isFailed}>Payer Details</StyledH3>
                    </div>
                    <HorizontalDivider />
                    <Row xs={4}>
                        <Col className="border-end border-humble-grey">
                            <dt>
                                <p className="mt-1 mb-2 text-label-grey">
                                    Requested Date
                                </p>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {formatToDate(paymentOrderState.props.valueDate)}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <p className="mt-1 mb-2 text-label-grey">
                                    Payer Bank Account
                                </p>
                            </dt>
                            {!paymentOrderState.props.fromAccount?.accountName &&
                            !paymentOrderState.props.fromAccount?.accountNumber ? (
                                <dd><p className="fw-bold">-</p></dd>
                            ) : (
                                <dd>
                                    <p className="fw-bold mb-0">
                                        {
                                            paymentOrderState.props.fromAccount
                                                ?.accountName
                                        }
                                    </p>
                                    <p className="fw-bold">
                                        {
                                            paymentOrderState.props.fromAccount
                                                ?.accountNumber
                                        }
                                    </p>
                                </dd>
                            )}
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <p className="mt-1 mb-2 text-label-grey">
                                    Payer Bank
                                </p>
                            </dt>
                            {!paymentOrderState.props.fromAccount?.bic &&
                            !paymentOrderState.props.payer?.legalName ? (
                                <dd><p className="fw-bold">-</p></dd>
                            ) : (
                                <dd>
                                    <p className="fw-bold">
                                        {paymentOrderState.props.fromAccount?.bic} -{" "}
                                        {paymentOrderState.props.payer.legalName}
                                    </p>
                                </dd>
                            )}
                        </Col>
                        <Col className="px-4">
                            <dt>
                                <p className="mt-1 mb-2 text-label-grey">
                                    Payer Address
                                </p>
                            </dt>
                            {!paymentOrderState.props.payer?.addressLn1 &&
                            !paymentOrderState.props.payer?.addressLn2 ? (
                                <dd><p className="fw-bold">-</p></dd>
                            ) : (
                                <dd>
                                    <p className="fw-bold">
                                        {paymentOrderState.props.payer?.addressLn1}
                                        {paymentOrderState.props.payer?.addressLn2 && (
                                            <>
                                                <br />{" "}
                                                {
                                                    paymentOrderState.props.payer
                                                        ?.addressLn2
                                                }
                                            </>
                                        )}
                                    </p>
                                </dd>
                            )}
                        </Col>
                    </Row>
                    <HorizontalDivider />
                    <Row>
                        <Col>
                            <h3 className="my-2">
                                Payment Order Approvers
                            </h3>
                            <PaymentApproversTable
                                data={paymentApprovers}
                                comments={paymentOrderState.comments}
                            />
                        </Col>
                    </Row>
                    <HorizontalDivider />

                    <div className="my-3">
                        <StyledH3 isFailed={isFailed}>Payment Details</StyledH3>
                        <dd>
                            <p className="mb-2">
                                <span className="h1">
                                    {formatAmount(
                                        paymentOrderState.props.instructedMVUnit,
                                    )}
                                </span>
                                <span className="ms-2 fw-bold">
                                    {paymentOrderState.props.instructedMVCurrency}
                                </span>
                            </p>
                        </dd>
                        <dt>
                            <p className="text-label-grey mb-5">Payable Amount</p>
                        </dt>
                    </div>

                    <dt><p className="text-label-grey mb-2">Payment Type</p></dt>
                    <dd>
                        <p className="fw-bold mb-3">-</p>
                    </dd>
                    <dt><p className="text-label-grey mb-2">Charge Bearer</p></dt>
                    <dd>
                        <p className="fw-bold mb-3">
                            {paymentOrderState.props.chargeBearer}
                        </p>
                    </dd>
                    <dt><p className="text-label-grey mb-2">Purpose Code</p></dt>
                    <dd>
                        <p className="fw-bold mb-3">
                            {
                                PurposeCodeOptions.find(
                                    (code) =>
                                        code.value ===
                                        paymentOrderState.props.purposeCode,
                                )?.label
                            }
                            {
                                CNYPurposeCodeOptions.find(
                                    (code) =>
                                        code.value ===
                                        paymentOrderState.props.purposeCode,
                                )?.label
                            }
                        </p>
                    </dd>

                    <dt><p className="text-label-grey mb-2">Housing Rules Type</p></dt>
                    <dd>
                        <div className="mb-5">
                            <HousingRulesText
                                className="fw-bold"
                                rules={paymentOrderState.props.bankInformation}
                            />
                        </div>
                    </dd>
                    <dt>
                        <p className="text-label-grey mb-2">
                            Remittance Information
                        </p>
                    </dt>
                    <dd>
                        <p className="text-pre-wrap fw-bold mb-5">
                            {paymentOrderState.props.remittanceInformation.map(
                                (it, index) => (
                                    <Fragment key={index}>
                                        {it}
                                        <br />
                                    </Fragment>
                                ),
                            )}
                        </p>
                    </dd>

                    {sharedDocument.length > 0 && (
                        <StyledH3 isFailed={isFailed}>
                            {pluralize("Invoice", sharedDocument.length)} Shared
                            with Bank
                        </StyledH3>
                    )}
                    {sharedDocument.map((file, index) => (
                        <FileRow
                            key={index}
                            file={{
                                id: file.documentHash,
                                name: file.documentName,
                            }}
                            canDownload
                            downloadMeta={{
                                developerAccountName,
                                contractorAccountName,
                            }}
                        />
                    ))}
                </dl>
            </Card.Body>
        </Card>
    );
};

const StyledH3 = styled.h3<{ isFailed?: boolean }>`
    margin-bottom: 1rem;
    color: ${(props) =>
        props.isFailed ? "var(--bs-placeholder-grey)" : "var(--bs-body-color)"};
`;

export default PaymentOrderDetail;
